import React, { useEffect, useState } from "react";
import { isLoginHSProduct } from "../config/product";
import TermsOfServiceBody from "./TermsOfServiceBody";
import { reportSignupConversion } from "../adsConversion";
import { setToken } from "../audit/authState";  // 설계 v7 §4-3 래퍼
import { CONSENT_TITLE, CONSENT_LINES, MEMBER_ROLES, savePendingConsent, lookupBusinessPublic, registerCompanyAtSignup } from "../audit/disclosureConsent";

// ──────────────────────────────────────────────────────
// SnsSignupPage.jsx — SNS 신규 사용자 간단 회원가입
//   OAuth 콜백에서 신규(미가입) SNS 사용자를 자동가입시키지 않고 이 화면으로 보냄.
//   백엔드가 sns_signup 쿠키(서명 토큰)에 프로필을 담아 전달 → GET /sns/pending 로 표시.
//   약관 동의 + 개인/사업자 선택 후 POST /sns/complete → 계정 생성 + 로그인.
//   톤 = LoginHS 절제(검정·회색).
// ──────────────────────────────────────────────────────

const INK = "#16151a";
const DARK = "#1b1a20";
const LINE = "#e5e7eb";
const MUTED = "#6b7280";

const PROVIDER_LABEL = { google: "구글", naver: "네이버", kakao: "카카오" };

const SnsSignupPage = ({ API_BASE_URL, onLoginSuccess, onNavigate }) => {
  const BRAND = isLoginHSProduct() ? "LoginHS" : "DocuAI";
  const [pending, setPending] = useState(null); // null=로딩 / {success,...}
  const [userName, setUserName] = useState("");
  const [isCompany, setIsCompany] = useState(false);
  const [businessNumber, setBusinessNumber] = useState("");
  // 2026-10-03: SNS 로 회사 가입해도 이메일 가입과 같이 회원 구분·정보공개 동의·회사 등록을 한다(가입자 입력은 최소로).
  const [memberRole, setMemberRole] = useState("");
  const [agreeDisclosure, setAgreeDisclosure] = useState(false);
  const [showDisclosureText, setShowDisclosureText] = useState(false);
  const [agreeYn, setAgreeYn] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/auth/sns/pending`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        setPending(d);
        if (d.success) setUserName(d.name || "");
      })
      .catch(() =>
        setPending({ success: false, message: "정보를 불러오지 못했습니다." }),
      );
  }, [API_BASE_URL]);

  const goLogin = () => {
    window.history.replaceState({}, "", "/");
    if (onNavigate) onNavigate("login");
  };

  const handleSubmit = async () => {
    setError("");
    if (!userName.trim()) return setError("이름을 입력해주세요.");
    if (isCompany && businessNumber.replace(/[^0-9]/g, "").length !== 10)
      return setError("사업자등록번호 10자리를 정확히 입력해주세요.");
    if (isLoginHSProduct() && isCompany && !memberRole)
      return setError("회원 구분(화주·관세사)을 선택해주세요.");
    if (!agreeYn) return setError("약관 및 개인정보 처리방침에 동의해주세요.");
    if (!agreeMarketing)
      return setError(
        "마케팅·홍보 정보 수신에 동의해야 무상 이용이 가능합니다.",
      );
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/sns/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userName: userName.trim(),
          isCompany,
          businessNumber: isCompany ? businessNumber : "",
          agreeYn,
          agreeMarketing,
        }),
      });
      const data = await res.json();
      if (data.success && data.token) {
        reportSignupConversion();
        setToken(data.token, "local");
        localStorage.setItem("groupCode", data.user?.groupCode || "");
        localStorage.setItem("userId", data.user?.userId || "");
        if (isLoginHSProduct() && isCompany) {
          const bizNo = businessNumber.replace(/\D/g, "");
          if (agreeDisclosure) savePendingConsent({ businessNumber: bizNo, agreed: true });
          // 회사 정보는 사업자번호 조회로 채워 검토 대기로 남긴다 — 실패해도 가입은 끝난다(회사 계정 화면에서 다시 요청).
          try {
            let info = {};
            try { info = (await lookupBusinessPublic(bizNo)) || {}; } catch (_e) { info = {}; }
            await registerCompanyAtSignup({
              group_id: data.user?.groupCode, user_id: data.user?.userId, business_no: bizNo, role: memberRole,
              corp_name: info.corp_name, ceo_name: info.ceo_name, corp_addr: info.corp_addr, contact_name: userName.trim(),
              corp_name_en: info.corp_name_en, customs_office: info.customs_office, nts_status: info.nts_status,
              biz_type: info.biz_type, biz_item: info.biz_item,
            });
          } catch (_e) { /* 로그인 뒤 회사 계정 화면에서 다시 요청할 수 있다 */ }
        }
        window.history.replaceState({}, "", "/");
        if (onLoginSuccess)
          onLoginSuccess({
            groupCode: data.user?.groupCode,
            userId: data.user?.userId,
          });
      } else {
        setError(data.message || "회원가입에 실패했습니다.");
        setLoading(false);
      }
    } catch (e) {
      setError("회원가입 처리 중 오류가 발생했습니다.");
      setLoading(false);
    }
  };

  const card = {
    width: "100%",
    maxWidth: 420,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 16,
    padding: "32px 28px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
  };
  const label = {
    fontSize: 13,
    fontWeight: 700,
    color: INK,
    margin: "0 0 6px",
    display: "block",
  };
  const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 13px",
    fontSize: 14,
    border: `1px solid ${LINE}`,
    borderRadius: 9,
    outline: "none",
    fontFamily: "inherit",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #f8fafc 0%, #eef0f4 100%)",
        padding: 16,
        fontFamily:
          "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={card}>
        {pending === null ? (
          <div style={{ textAlign: "center", color: MUTED, fontSize: 14 }}>
            불러오는 중…
          </div>
        ) : !pending.success ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>
              회원가입 정보를 불러올 수 없습니다
            </div>
            <p style={{ fontSize: 13, color: MUTED, margin: "10px 0 18px" }}>
              {pending.message || "다시 로그인해주세요."}
            </p>
            <button onClick={goLogin} style={{ ...primaryBtn() }}>
              로그인 화면으로
            </button>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>
                {BRAND} 회원가입
              </div>
              <p style={{ fontSize: 13, color: MUTED, margin: "8px 0 0" }}>
                {PROVIDER_LABEL[pending.provider] || "SNS"} 계정으로 처음
                오셨네요. 간단한 정보만 확인하면 바로 시작합니다.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={label}>이메일</label>
              <input
                style={{ ...input, background: "#f3f4f6", color: MUTED }}
                value={pending.email || "(SNS에서 이메일 미제공)"}
                readOnly
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={label}>이름</label>
              <input
                style={input}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="이름"
                maxLength={50}
              />
            </div>

            {/* 개인 / 사업자 */}
            <div style={{ marginBottom: 16 }}>
              <label style={label}>가입 유형</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { v: false, t: "개인" },
                  { v: true, t: "사업자" },
                ].map((o) => {
                  const active = isCompany === o.v;
                  return (
                    <button
                      key={o.t}
                      type="button"
                      onClick={() => setIsCompany(o.v)}
                      style={{
                        flex: 1,
                        padding: "10px 0",
                        fontSize: 14,
                        fontWeight: 700,
                        borderRadius: 9,
                        cursor: "pointer",
                        border: `1px solid ${active ? DARK : LINE}`,
                        background: active ? DARK : "#fff",
                        color: active ? "#fff" : INK,
                      }}
                    >
                      {o.t}
                    </button>
                  );
                })}
              </div>
            </div>

            {isCompany && (
              <div style={{ marginBottom: 16 }}>
                <label style={label}>사업자등록번호</label>
                <input
                  style={input}
                  value={businessNumber}
                  onChange={(e) => setBusinessNumber(e.target.value)}
                  placeholder="숫자 10자리"
                  inputMode="numeric"
                  maxLength={12}
                />
                <p style={{ fontSize: 12, color: MUTED, margin: "6px 0 0" }}>
                  같은 사업자번호로 가입하면 같은 그룹으로 묶입니다.
                </p>
                {isLoginHSProduct() && (
                  <div style={{ marginTop: 14 }} data-testid="sns-member-role">
                    <label style={label}>회원 구분</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {MEMBER_ROLES.map((r) => {
                        const active = memberRole === r.id;
                        return (
                          <button key={r.id} type="button" role="radio" aria-checked={active} onClick={() => setMemberRole(r.id)}
                            style={{ flex: 1, padding: "9px 0", fontSize: 13, fontWeight: 700, borderRadius: 9, cursor: "pointer",
                              border: `1px solid ${active ? DARK : LINE}`, background: active ? DARK : "#fff", color: active ? "#fff" : INK }}>
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                    <p style={{ fontSize: 12, color: MUTED, margin: "6px 0 0" }}>
                      회사 정보는 사업자번호로 자동 조회해 등록합니다(검토 후 확인).
                    </p>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, cursor: "pointer", fontSize: 13, color: INK }}
                      data-testid="sns-disclosure">
                      <input type="checkbox" checked={agreeDisclosure} onChange={(e) => setAgreeDisclosure(e.target.checked)} style={{ marginTop: 2 }} />
                      <span>
                        (선택) {CONSENT_TITLE}{" "}
                        <button type="button" onClick={(e) => { e.preventDefault(); setShowDisclosureText((v) => !v); }}
                          style={{ border: 0, background: "none", padding: 0, color: MUTED, textDecoration: "underline", cursor: "pointer", fontSize: 12 }}>
                          {showDisclosureText ? "접기" : "전문 보기"}
                        </button>
                      </span>
                    </label>
                    {showDisclosureText && (
                      <div style={{ margin: "6px 0 0 24px", padding: "8px 10px", borderLeft: `2px solid ${LINE}`, fontSize: 12, color: "#4b5563", lineHeight: 1.6 }}>
                        {CONSENT_LINES.map((l, i) => <div key={i}>{l}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                margin: "4px 0 18px",
                cursor: "pointer",
                fontSize: 13,
                color: INK,
              }}
            >
              <input
                type="checkbox"
                checked={agreeYn}
                onChange={(e) => setAgreeYn(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
              />
              <span>
                <b>(필수)</b> DocuAI·LoginHS 통합 이용약관 및 개인정보
                처리방침에 동의합니다.{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTerms((v) => !v);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: DARK,
                    fontWeight: 700,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                  }}
                >
                  {showTerms ? "접기" : "전문 보기"}
                </button>
              </span>
            </label>

            {showTerms && (
              <div
                style={{
                  maxHeight: 220,
                  overflowY: "auto",
                  border: `1px solid ${LINE}`,
                  borderRadius: 10,
                  background: "#fafbfd",
                  margin: "-6px 0 16px",
                }}
              >
                <TermsOfServiceBody />
              </div>
            )}

            {/* 무상 제공 조건 — 마케팅 동의 (정보통신망법상 별도 동의) */}
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                margin: "0 0 6px",
                cursor: "pointer",
                fontSize: 13,
                color: INK,
              }}
            >
              <input
                type="checkbox"
                checked={agreeMarketing}
                onChange={(e) => setAgreeMarketing(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
              />
              <span>
                <b>(필수)</b> 마케팅·홍보 정보 수신에 동의합니다
                (이메일·문자·전화 등).
              </span>
            </label>
            <p
              style={{
                fontSize: 12,
                color: MUTED,
                margin: "0 0 16px 24px",
                lineHeight: 1.5,
              }}
            >
              LoginHS HS 품목분류는 <b style={{ color: INK }}>무상 제공</b>되며,
              마케팅·홍보 정보 수신 동의를 조건으로 합니다. 수신
              거부(옵트아웃)는 언제든 가능합니다.
            </p>

            {error && (
              <div
                style={{
                  fontSize: 13,
                  color: "#d93025",
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                ...primaryBtn(),
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "처리 중…" : "가입하고 시작하기"}
            </button>

            <button
              onClick={goLogin}
              style={{
                width: "100%",
                marginTop: 10,
                background: "transparent",
                border: "none",
                color: MUTED,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              취소하고 로그인 화면으로
            </button>
          </>
        )}
      </div>
    </div>
  );
};

function primaryBtn() {
  return {
    width: "100%",
    padding: "13px 0",
    background: DARK,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  };
}

export default SnsSignupPage;
